import Alert from '@components/alert/alert';
import {
	FileUploader,
	FileUploaderView,
	FileUploadStatus,
	TextArea,
	type UploadedFile,
} from '@gurupay/gp-ui-library';
import { useRejectedFilesNotify } from '@hooks/useRejectedFilesNotify.ts';
import { useUpdateThread } from '@hooks/useUpdateThread';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ALLOWED_MIMES } from './constants';
import { MessageTfa } from './MessageTfa';

interface FormValues {
	content: {
		text: string;
	};
}

interface ThreadReplyFormProps {
	isClosed?: boolean;
	isFetching: boolean;
	threadId: string;
	topic?: string;
}

const ThreadReplyForm = ({
	isClosed,
	isFetching,
	threadId,
	topic,
}: ThreadReplyFormProps) => {
	const { t } = useTranslation('', { keyPrefix: 'messages' });
	const { t: tFileInput } = useTranslation('', { keyPrefix: 'fileInput' });
	const [signingInitiated, setSigningInitiated] = useState(false);
	const [files, setFiles] = useState<File[]>([]);
	const displayFiles: UploadedFile[] = files.map((file, index) => ({
		id: `${index}_${file.name}`,
		name: file.name,
		size: file.size,
		status: FileUploadStatus.SUCCESS,
	}));
	const { handleReject } = useRejectedFilesNotify({ size: 2 });

	const { control, handleSubmit, formState, watch, getValues, reset } =
		useForm<FormValues>({
			mode: 'onBlur',
			defaultValues: { content: { text: '' } },
		});

	const resetForm = () => {
		setSigningInitiated(false);
		setFiles([]);
		reset();
	};
	const afterSignReply = useUpdateThread(threadId, resetForm);

	const newThreadMessage = watch('content.text');
	const errors = useMemo(() => formState.errors, [formState]);

	const handleRemoveFile = (id: string) => {
		setFiles((prev) => prev.filter((_, i) => i !== Number(id.split('_')[0])));
	};
	const handleFilesChange = (file: File[]) => setFiles((prev) => [...prev, ...file]);

	return (
		<form className="[@media(min-height:700px)]:sticky min-mobile:sticky bottom-0 bg-white py-space-16 px-space-24 -mx-6">
			{isClosed ? (
				<div className="border-t border-neutral-50 pt-space-24">
					<Alert className="mx-auto" type="info">
						{t('closed')}
					</Alert>
				</div>
			) : (
				<>
					<Controller
						control={control}
						name="content.text"
						render={({ field }) => (
							<TextArea
								id="reply"
								{...field}
								error={errors?.content?.text?.message}
								isDisabled={signingInitiated}
								isRequired
								label={t('reply')}
							/>
						)}
						rules={{
							required: t('form.required'),
							minLength: {
								value: 4,
								message: t('form.minLength', { value: 4 }),
							},
						}}
					/>
					<FileUploader
						className="my-space-16"
						files={displayFiles}
						fileTypes={ALLOWED_MIMES}
						hint={tFileInput('fileInfo')}
						isDisabled={signingInitiated}
						itemClassName="max-w-full"
						label={
							<p className="text-sm-medium">
								<span className="font-bold">{tFileInput('actionDesktop')} </span>
								{tFileInput('actionDesktopSmall')}
							</p>
						}
						listClassName="flex gap-space-8 flex-wrap"
						maxFiles={5}
						maxSizeMB={2}
						onFileReject={handleReject}
						onFileRemove={handleRemoveFile}
						onFileSelect={handleFilesChange}
						view={FileUploaderView.COMPACT}
					/>
					<MessageTfa
						afterSign={afterSignReply.mutate}
						files={files}
						getValues={getValues}
						isLoading={afterSignReply.isPending || isFetching}
						isValidFields={newThreadMessage.length >= 4}
						onInitiateSigning={handleSubmit(() => {
							setSigningInitiated(true);
						})}
						setSigningInitiated={setSigningInitiated}
						topic={topic}
					/>
				</>
			)}
		</form>
	);
};

export { ThreadReplyForm };
